import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconLockCloseFilled, IconTimesDefault } from '@iwoca/icons';
import {
  Button,
  Card,
  CheckBox,
  Input,
  Message,
  Modal,
  Select,
  Spinner,
} from '@iwoca/orion';
import clsx from 'clsx';

import { useAmplitude } from 'hooks/useAmplitude';
import { useInvalidateAuth } from 'hooks/useAuth';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { TRegistrationFormDataDE } from '../registration.types';
import styles from '../RegistrationForm.module.css';
import {
  fullRegistrationSchemaDE,
  getBranchSchemaDE,
  personalInformationSchemaDE,
} from '../registrationSchema';
import { useCompanyRegistrationDE } from '../useCompanyRegistrationDE';
import { submitDERegistration } from './RegistrationFormDE.helpers';

export const RegistrationFormDE = () => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAmplitude();
  const inviteId = searchParams.get('introducer') as string | undefined;
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isValidBranch, setIsValidBranch] = useState(false);
  const navigate = useNavigate();
  const invalidateAuth = useInvalidateAuth();
  const { company, availableBranches, isLoadingBranches } =
    useCompanyRegistrationDE();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TRegistrationFormDataDE>({
    defaultValues: {
      introducerType: 'broker',
    },
    resolver: yupResolver(
      company
        ? getBranchSchemaDE(
            company.emailValidation,
            company.emailExample,
            company.branchLabel,
          )
        : inviteId
          ? personalInformationSchemaDE
          : fullRegistrationSchemaDE,
    ),
  });

  // Set branchUUID if both company and inviteId are present
  useEffect(() => {
    if (company && inviteId) {
      const matchingBranch = availableBranches?.find(
        (branch) => branch.uuid === inviteId,
      );
      if (matchingBranch) {
        setValue('branchUUID', inviteId);
        setIsValidBranch(true);
      } else {
        setIsValidBranch(false);
      }
    }
  }, [company, inviteId, availableBranches, setValue]);

  const onSubmit = async (values: TRegistrationFormDataDE) => {
    if (!isConfirming) {
      setIsConfirming(true);
      return;
    }

    setIsLoading(true);

    const handleSuccess = async () => {
      trackEvent(AMPLITUDE_EVENTS.introducerRegisterSubmitted, {
        'Introducer type': values.introducerType,
        'Invite link': inviteId || null,
      });

      // Refresh auth before redirecting; otherwise ProductsLoader sees
      // the cached anonymous result and bounces the new user to /403.
      await invalidateAuth();
      navigate(PATH_NAMES.home());
    };

    const registeredTradingAddress = `${values.addressLine1} ${values.addressLine2}, ${values.town}`;

    await submitDERegistration({
      values: {
        ...values,
        registeredTradingAddress,
        uuid: values.branchUUID || inviteId,
      },
      onSuccess: handleSuccess,
      onError: () => {
        setShowErrorModal(true);
      },
    });

    setIsLoading(false);
  };

  const renderCompanyInfo = () => {
    return (
      <>
        <h2>Informationen über das Unternehmen</h2>
        <Card className={styles.companyInfoContainer}>
          <div className={styles.formField}>
            <Input
              {...register('companyName')}
              id="companyName"
              name="companyName"
              type="text"
              label="Firmenname"
              placeholder="Iwoca"
              disabled={isConfirming}
              error={errors.companyName && errors.companyName.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('vatNumber')}
              id="vatNumber"
              name="vatNumber"
              type="text"
              label="Ust. Nummer (optional)"
              placeholder="123456"
              disabled={isConfirming}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyNumber')}
              id="companyNumber"
              name="companyNumber"
              type="text"
              label="Handelsregisternummer (HR) (optional)"
              placeholder="12345678"
              disabled={isConfirming}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyWebsite')}
              id="companyWebsite"
              name="companyWebsite"
              type="text"
              label="Webseite (optional)"
              placeholder="www.iwoca.de"
              disabled={isConfirming}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine1')}
              id="addressLine1"
              name="addressLine1"
              type="text"
              label="Straße"
              placeholder="Frankfurter Straße"
              disabled={isConfirming}
              error={errors.addressLine1 && errors.addressLine1.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine2')}
              id="addressLine2"
              name="addressLine2"
              type="text"
              label="Hausnummer"
              placeholder="12"
              disabled={isConfirming}
              error={errors.addressLine2 && errors.addressLine2.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('postcode', {
                setValueAs: (text: string) => text.toUpperCase(),
              })}
              id="postcode"
              name="postcode"
              type="text"
              label="Postleitzahl"
              placeholder="12345"
              disabled={isConfirming}
              error={errors.postcode && errors.postcode.message}
              className={styles.uppercaseText}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('town')}
              id="town"
              name="town"
              type="text"
              label="Stadt"
              placeholder="Frankfurt"
              disabled={isConfirming}
              error={errors.town && errors.town.message}
            />
          </div>
        </Card>
      </>
    );
  };

  const renderAccountInfo = () => {
    return (
      <>
        <h2>Persönliche Informationen</h2>
        <Card className={styles.accountInformationContainer}>
          <div className={styles.formField}>
            <Select
              {...register('title')}
              label="Anrede"
              id="title"
              name="title"
              type="text"
              disabled={isConfirming}
            >
              <option value="Herr">Herr</option>
              <option value="Frau">Frau</option>
            </Select>
          </div>
          <div className={styles.formField}>
            <Input
              {...register('firstName')}
              id="firstName"
              name="firstName"
              type="text"
              label="Vorname"
              placeholder="Max"
              disabled={isConfirming}
              error={errors.firstName && errors.firstName.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('lastName')}
              id="lastName"
              name="lastName"
              type="text"
              label="Nachname"
              placeholder="Mustermann"
              disabled={isConfirming}
              error={errors.lastName && errors.lastName.message}
            />
          </div>
          <div className={clsx(styles.formField, styles.email)}>
            <Input
              {...register('email')}
              id="email"
              name="email"
              type="text"
              label="E-Mail"
              placeholder={company?.emailPlaceholder || 'ich@iwoca.de'}
              disabled={isConfirming}
              error={errors.email && errors.email.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('phoneNumber')}
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              label="Telefonnummer"
              placeholder="+49 123456789"
              disabled={isConfirming}
              error={errors.phoneNumber && errors.phoneNumber.message}
            />
          </div>
          {company && (
            <div className={clsx(styles.formField, styles.branch)}>
              {isLoadingBranches ? (
                <Spinner
                  className={styles.spinner}
                  size="small"
                  data-testid="branch-spinner"
                />
              ) : (
                <Select
                  {...register('branchUUID')}
                  label={company.branchLabel}
                  id="branchUUID"
                  name="branchUUID"
                  type="text"
                  disabled={isConfirming || isValidBranch}
                  placeholder="Wählen Sie eine Option aus"
                  error={errors.branchUUID && errors.branchUUID.message}
                >
                  {(availableBranches ?? []).map((branch) => (
                    <option key={branch.number} value={branch.uuid}>
                      {branch.name} | {branch.number}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          )}
        </Card>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <h1>Erstellen Sie ein Partnerprofil</h1>
      {!inviteId && !company && (
        <p className={styles.inviteInformation}>
          Ist Ihr Unternehmen bereits ein Partner von uns? Lassen Sie sich von
          Ihren Kolleg*innen einen Einladungslink geben, um sofort loszulegen.
        </p>
      )}
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {!inviteId && !company && renderCompanyInfo()}
        {renderAccountInfo()}
        <div className={styles.footerItem}>
          <div className={styles.privacyPolicyAgreement}>
            <CheckBox
              {...register('privacyPolicyAgreement')}
              id="privacyPolicyAgreement"
              name="privacyPolicyAgreement"
              data-testid="privacyPolicyAgreement"
              disabled={isConfirming}
            />
            <div className={styles.privacyPolicyDescription}>
              <p>
                Ich habe die{' '}
                <a
                  href="https://static.iwoca.com/pdf/de/iwoca_Partner_AGB_v20240816.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  iwoca Partner AGB
                </a>{' '}
                gelesen und stimme diesen zu.
              </p>
              {errors.privacyPolicyAgreement && (
                <Message
                  message={errors.privacyPolicyAgreement.message || ''}
                  type="error"
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.footerItem}>
          <Button
            variant={isConfirming ? 'success' : 'primary'}
            type="submit"
            loading={isLoading}
          >
            {isConfirming ? 'Bestätigen' : 'Anmelden'}
            <IconLockCloseFilled />
          </Button>
          {isConfirming && (
            <Button
              className={styles.cancelButton}
              variant="tertiary"
              type="button"
              onClick={() => setIsConfirming(false)}
            >
              Abbrechen
              <IconTimesDefault />
            </Button>
          )}
        </div>
        <div className={styles.footerItem}>
          <p className={styles.submitDescription}>
            Auf eine erfolgreiche Partnerschaft!
          </p>
        </div>
      </form>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Es gibt ein Problem mit den Daten, die Sie eingegeben haben."
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          Wenn Sie ein Konto bei iwoca haben, melden Sie sich über den
          Login-Button oben rechts an. Wenn nicht, dann rufen Sie uns gerne an
          und wir helfen Ihnen bei der Anmeldung.
        </p>
      </Modal>
    </div>
  );
};
